import { useState } from 'react'
import BranchSelector from './BranchSelector'
import SchedulePicker from './SchedulePicker'
import WebhookSetup   from './WebhookSetup'
import TestPlanRulePanel from '../testplan-rule/TestPlanRulePanel'
import { useAutomation } from '../../hooks/useAutomation'
import toast from 'react-hot-toast'
import clsx from 'clsx'

/**
 * RepositoryAutomationPanel
 *
 * Manages automation settings for a repository.
 *
 * Document + Specification automation:
 *   Controlled via the existing schedule/webhook toggles below.
 *
 * Test Plan generation:
 *   Has been separated into TestPlanRulePanel (rendered at the bottom).
 *   It is NOT part of the schedule or webhook output toggles.
 *   Reason: test plans are only generated on explicit request or when
 *   code reaches a specific gated branch — never on every push/schedule.
 *
 * Props:
 *   repositoryId  — number
 *   provider      — 'github' | 'gitlab' | 'bitbucket'
 *   fullName      — string  (e.g. 'org/repo')
 *   defaultBranch — string
 *   onClose       — () => void
 */
export default function RepositoryAutomationPanel({ repositoryId, provider, fullName, defaultBranch, onClose }) {
  const { automation, loading, saving, save } = useAutomation(repositoryId)
  const [form, setForm] = useState(null)

  const current = form ?? automation
  const isDirty = form !== null

  const set = (key, value) => setForm(prev => ({ ...(prev ?? automation), [key]: value }))

  const handleSave = async () => {
    const result = await save(form)
    if (result.ok) {
      toast.success('Automation settings saved.')
      setForm(null)
    } else {
      toast.error('Failed to save settings.')
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
  )

  return (
    <div className="space-y-6">

      {/* ── Branch selector ────────────────────────────────────────────── */}
      <div>
        <label className="text-xs font-bold text-white/50 uppercase tracking-widest block mb-3">
          Watched Branch
        </label>
        <BranchSelector
          repositoryId={repositoryId}
          value={current?.auto_generate_branch ?? defaultBranch}
          onChange={branch => set('auto_generate_branch', branch)}
        />
        <p className="text-[11px] text-white/25 mt-2">
          Branch monitored for document and specification auto-generation.
        </p>
      </div>

      {/* ── Output toggles (docs + specs only) ─────────────────────────── */}
      <div>
        <label className="text-xs font-bold text-white/50 uppercase tracking-widest block mb-3">
          What to Auto-Generate
        </label>

        <div className="space-y-2">
          {[
            { key: 'auto_gen_documents',     label: 'Business Documents',   desc: 'Non-technical flow docs and API references' },
            { key: 'auto_gen_specifications', label: 'Specifications',       desc: 'Functional, technical, and user-story specs' },
          ].map(item => (
            <label
              key={item.key}
              className={clsx(
                'flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all',
                current?.[item.key]
                  ? 'border-purple-500/30 bg-purple-500/6'
                  : 'border-white/6 bg-white/[0.02] hover:border-white/12'
              )}
            >
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={!!current?.[item.key]}
                  onChange={e => set(item.key, e.target.checked)}
                />
                <div className={clsx(
                  'w-4 h-4 rounded border-2 flex items-center justify-center transition-all',
                  current?.[item.key] ? 'bg-purple-500 border-purple-500' : 'border-white/20'
                )}>
                  {current?.[item.key] && (
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  )}
                </div>
              </div>
              <div>
                <p className={clsx('text-sm font-semibold', current?.[item.key] ? 'text-white' : 'text-white/55')}>
                  {item.label}
                </p>
                <p className="text-[11px] text-white/30 mt-0.5">{item.desc}</p>
              </div>
            </label>
          ))}

          {/* Test plans — explicit info callout instead of a toggle */}
          <div className="flex items-start gap-3 p-3.5 rounded-xl border border-dashed border-amber-500/20 bg-amber-500/4">
            <span className="text-base flex-shrink-0 mt-0.5">🧪</span>
            <div>
              <p className="text-sm font-semibold text-amber-300/80">Test Plans</p>
              <p className="text-[11px] text-white/35 mt-0.5 leading-relaxed">
                Test plans are <strong className="text-white/50">never</strong> part of the automatic generation flow.
                Configure when and how they generate using the dedicated rule panel below.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Schedule picker ─────────────────────────────────────────────── */}
      <SchedulePicker
        value={current?.auto_generate_schedule ?? 'disabled'}
        scheduleTime={current?.schedule_time}
        scheduleDay={current?.schedule_day}
        webhookEnabled={current?.webhook_enabled}
        onChange={(k, v) => set(k, v)}
      />

      {/* ── Webhook setup ───────────────────────────────────────────────── */}
      <WebhookSetup
        repositoryId={repositoryId}
        provider={provider}
        fullName={fullName}
        enabled={current?.webhook_enabled}
        onToggle={enabled => set('webhook_enabled', enabled)}
      />

      {/* ── Save bar ────────────────────────────────────────────────────── */}
      {isDirty && (
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/8 animate-fade-in">
          <p className="text-xs text-white/35">Unsaved changes</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setForm(null)}
              className="px-4 py-2 text-xs font-semibold text-white/40 hover:text-white transition-colors"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-specze text-white text-xs px-4 py-2 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ── Divider ─────────────────────────────────────────────────────── */}
      <div className="glow-divider" />

      {/* ── Test plan rule panel (fully separate) ───────────────────────── */}
      <div>
        <p className="text-xs font-bold text-white/50 uppercase tracking-widest mb-3">
          Test Plan Generation Rule
        </p>
        <TestPlanRulePanel
          repositoryId={repositoryId}
          defaultBranch={defaultBranch}
          repoFullName={fullName}
        />
      </div>
    </div>
  )
}
